import React from "react";
import { useTranslation } from "react-i18next";
import type { TransformedProduct } from "../../../../types/productApiTypes";
import ProductPriceDisplay from "../../ProductPriceDisplay";

interface ProductPriceDetailsProps {
  product: TransformedProduct;
  selectedVariation: TransformedProduct | null;
}

/**
 * Handles the layout of the price section in the single product view.
 * Wraps the common ProductPriceDisplay with context-specific badges (No Stock, Low Stock or Sale %).
 */
const ProductPriceDetails: React.FC<ProductPriceDetailsProps> = ({
  product,
  selectedVariation,
}) => {
  const { t } = useTranslation();

  // Determine which data source (variation or parent) to use for badges
  const displayData = selectedVariation || product;

  // Check if low stock remaining exists and we are technically in stock
  // Note: If we are on backorder, we usually don't show "Only 2 left!"
  const isLowStock =
    displayData.lowStockRemaining !== null &&
    displayData.stockStatus === "instock";

  return (
    <div className="product-info-details__price-wrapper">
      {/* Price Section */}
      <ProductPriceDisplay
        product={product}
        selectedVariation={selectedVariation}
        className="product-info-details__price"
      />

      {/* Badges Row - Placed below the price */}
      <div className="product-info-details__badges">
        {/* 1. Out of Stock Badge */}
        {displayData.stockStatus === "outofstock" && (
          <span className="product-info-details__stock-badge product-info-details__stock-badge--out-of-stock">
            {t("productCard.outOfStockBadge")}
          </span>
        )}

        {/* 2. Low Stock Badge */}
        {isLowStock && (
          <span className="product-info-details__stock-badge product-info-details__stock-badge--low-stock">
            {t("productCard.lowStockBadge", {
              count: displayData.lowStockRemaining ?? 0,
            })}
          </span>
        )}

        {/* 3. On Sale Percentage Badge */}
        {displayData.onSale && displayData.salePercentage > 0 && (
          <span className="product-info-details__sale-badge">
            -{displayData.salePercentage}%
          </span>
        )}
      </div>
    </div>
  );
};

export default ProductPriceDetails;
